/** Money arrives as paise. Nothing below formats anything but paise. */

const LAKH = 100_000;
const CRORE = 10_000_000;

export function rupees(paise: number): number {
  return paise / 100;
}

/** Full Indian grouping: 32000000 → "₹3,20,000" */
export function formatINR(paise: number): string {
  return `₹${Math.round(rupees(paise)).toLocaleString('en-IN')}`;
}

/**
 * Per-student rates, where paise matter: 2333 → "₹23.33", 2100 → "₹21".
 * Rounding these to whole rupees misstates the price the school agreed to.
 */
export function formatRate(paise: number): string {
  const value = rupees(paise);
  return `₹${
    Number.isInteger(value)
      ? value.toLocaleString('en-IN')
      : value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }`;
}

/** Compact for KPI tiles: 86000000 → "₹8.6L", 1250000000 → "₹1.25Cr" */
export function formatINRCompact(paise: number): string {
  const value = rupees(paise);
  if (value >= CRORE) return `₹${trim(value / CRORE)}Cr`;
  if (value >= LAKH) return `₹${trim(value / LAKH)}L`;
  if (value >= 1000) return `₹${Math.round(value).toLocaleString('en-IN')}`;
  return `₹${trim(value)}`;
}

function trim(value: number): string {
  return Number(value.toFixed(value < 10 ? 2 : 1))
    .toString()
    .replace(/\.0+$/, '');
}

export function formatNumber(value: number): string {
  return value.toLocaleString('en-IN');
}

/** Basis points → percentage string. 850 → "8.5%" */
export function formatBps(bps: number, digits = 1): string {
  return `${(bps / 100).toFixed(digits)}%`;
}

/** Signed change for KPI captions. 600 → "+6%" */
export function formatChange(bps: number | null): string | null {
  if (bps === null) return null;
  const pct = bps / 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${Number(pct.toFixed(1))}%`;
}

export function formatDate(iso: string | null, opts?: Intl.DateTimeFormatOptions): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', opts ?? { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatDateShort(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
}

export function formatDateTime(iso: string): string {
  return `${formatDateShort(iso)} · ${formatTime(iso)}`;
}

/** "Monday 1 September 2026" for the overview subtitle. */
export function formatLongDate(date = new Date()): string {
  return date.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

export function pluralise(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}
